/**
 * The notebook's opening sentence, composed from the result: « Ça tombe juste pour S et L. En M,
 * le tour 5 annonce 80 mailles, le calcul en donne 78. » Parts that quote the pattern (row labels)
 * are returned apart so they can be marked up as pattern text.
 */
import { diagnosticTitle } from '../diagnostics/catalog';
import { groupConcernsSize, type DiagnosticGroup } from '../diagnostics/group';
import { findingLabel } from '../engine/localize';
import type { CompileResult } from '../engine/types';
import { formatList, formatNumber, templateParts, translate, type Lang, type MessageKey } from '../i18n';
import type { AnalysisState } from './analysis';
import { sizeName } from './sizes';

export type SummaryPart = string | { verbatim: string };

export interface SizeStatus {
  size: number;
  name: string;
  errors: number;
  /** False when too little of the pattern was read to call this size right (see `readingLimited`). */
  checked: boolean;
}

/** Below this share of understood rows, or this many rows, the notebook never says « Ça tombe juste ». */
export const MIN_READ_RATIO = 0.8;
export const MIN_READ_ROWS = 3;

/**
 * True when the engine understood some rows but too few for the absence of errors to mean anything:
 * less than 80 % of the rows, or fewer than 3 rows while the text has more.
 */
export function readingLimited(result: CompileResult, analysis: AnalysisState): boolean {
  if (analysis !== 'rows') return false;
  const { ratio, n_understood, n_rows } = result.coverage;
  return ratio < MIN_READ_RATIO || (n_understood < MIN_READ_ROWS && n_rows > n_understood);
}

export interface Summary {
  sentences: SummaryPart[][];
  sizes: SizeStatus[];
  tone: 'ok' | 'error' | 'warning' | 'none';
}

function fill(lang: Lang, key: MessageKey, vars: Record<string, SummaryPart[] | string>): SummaryPart[] {
  const out: SummaryPart[] = [];
  for (const part of templateParts(lang, key)) {
    if (typeof part === 'string') out.push(part);
    else {
      const v = vars[part.name];
      if (typeof v === 'string') out.push(v);
      else if (v) out.push(...v);
    }
  }
  // Merge adjacent strings.
  return out.reduce<SummaryPart[]>((acc, p) => {
    const last = acc[acc.length - 1];
    if (typeof p === 'string' && typeof last === 'string') acc[acc.length - 1] = last + p;
    else if (p !== '') acc.push(p);
    return acc;
  }, []);
}

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
const lowerFirst = (s: string) => s.charAt(0).toLowerCase() + s.slice(1);

/** "le tour 43" / "les rangs 3-5" in French when the label is a French row word; the bare label otherwise. */
function rowPhrase(label: string | null, lineNo: number, lang: Lang, start: boolean): SummaryPart[] {
  if (!label) return [translate(lang, start ? 'sum.lineStart' : 'sum.line', { n: lineNo })];
  if (lang === 'fr') {
    const m = /^(rangs?|tours?|rgs?)\b/i.exec(label);
    if (m) {
      const plural = /s$/i.test(m[1]) && !/^rgs$/i.test(m[1]);
      const article = plural ? 'les ' : 'le ';
      return [start ? capitalize(article) : article, { verbatim: lowerFirst(label) }];
    }
  }
  return [{ verbatim: label }];
}

export function composeSummary(
  result: CompileResult,
  groups: readonly DiagnosticGroup[],
  analysis: AnalysisState,
  lang: Lang,
): Summary {
  const t = (key: MessageKey, vars?: Record<string, string | number>) => translate(lang, key, vars);
  const sizes = result.document.sizes;
  const single = sizes.length <= 1;
  const errors = groups.filter((g) => g.severity === 'error');
  const toCheck = groups.length - errors.length;
  // Too little was read: never « Ça tombe juste », the reading limit comes first (after the errors).
  const limited = readingLimited(result, analysis);
  const status: SizeStatus[] = sizes.map((s) => ({
    size: s.index,
    name: sizeName(result, s.index, lang),
    errors: errors.filter((g) => groupConcernsSize(g, s.index)).length,
    checked: !limited,
  }));

  if (analysis === 'none') return { sentences: [[t('sum.none')]], sizes: [], tone: 'none' };

  const sentences: SummaryPart[][] = [];
  if (errors.length === 0) {
    if (!limited) sentences.push([t(single ? 'sum.cleanOne' : analysis === 'module' ? 'sum.cleanModule' : 'sum.clean')]);
  } else {
    const clean = status.filter((s) => s.errors === 0).map((s) => s.name);
    if (!single && clean.length > 0 && !limited) sentences.push([t('sum.cleanFor', { sizes: formatList(lang, clean) })]);
    // One story per erroneous size, at most two, each told once.
    const told = new Set<string>();
    for (const s of status) {
      if (told.size >= 2) break;
      if (s.errors === 0) continue;
      const g = errors.find((e) => groupConcernsSize(e, s.size) && !told.has(e.key));
      if (!g) continue;
      told.add(g.key);
      const concerned = g.sizes === 'all' ? 'all' : g.sizes.map((i) => sizeName(result, i, lang));
      const where = single ? '' : concerned === 'all' ? t('sum.whereAll') : t('sum.whereSizes', { sizes: formatList(lang, concerned) });
      const label = findingLabel(g.line_no, g.label, result);
      const row = rowPhrase(label, g.line_no, lang, where === '');
      const valued = g.diagnostics.find((d) => (d.size === null || d.size === s.size) && d.expected != null && d.actual != null);
      if (valued && valued.expected != null && valued.actual != null) {
        sentences.push(
          fill(lang, 'sum.stated', {
            where,
            row,
            expected: formatNumber(lang, valued.expected, 2),
            actual: formatNumber(lang, valued.actual, 2),
            stitches: t(valued.expected === 1 ? 'sum.stitches1' : 'sum.stitchesN'),
          }),
        );
      } else {
        sentences.push(fill(lang, 'sum.problem', { where, row, title: lowerFirst(diagnosticTitle(g.code, lang)) }));
      }
    }
    const more = errors.length - told.size;
    if (more > 0) sentences.push([more === 1 ? t('sum.more1') : t('sum.moreN', { n: more })]);
  }
  if (limited) {
    const { n_understood: n, n_rows: total } = result.coverage;
    sentences.push([t(n === 1 ? 'sum.readOne' : n < MIN_READ_ROWS ? 'sum.readFew' : 'sum.readPart', { n, total })]);
    if (toCheck > 0) sentences.push([t('sum.readSee', { section: t('nb.toCheck') })]);
  } else if (toCheck > 0) sentences.push([toCheck === 1 ? t('sum.toCheck1') : t('sum.toCheckN', { n: toCheck })]);
  return {
    sentences,
    sizes: status,
    tone: errors.length > 0 ? 'error' : toCheck > 0 || limited ? 'warning' : 'ok',
  };
}

/** Plain-text form (tests, print title). */
export function summaryText(summary: Summary): string {
  return summary.sentences.map((s) => s.map((p) => (typeof p === 'string' ? p : p.verbatim)).join('')).join(' ');
}
